import { useFormat } from '@ideavivat/vivat-2';

import useSubJob from '@/use/useSubJob';
import useJobStatus from '@/use/useJobStatus';

const {
  translateSubJobStatus, statusClassMap,
} = useSubJob();
const { dateDifferenceDisplay } = useJobStatus();

const toNumberFormat = (val: string) => parseFloat(val || '0').toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/**
 * If the overall status is assembly, the computed status is done, or the process status is qc, the
 * status is qc. If the overall status is qc pass, the status is qc pass. If the overall status is qc
 * progress, the status is qc progress. If the overall status is qc fail, the status is qc fail. If the
 * overall status is revise or modify, the status is revise or modify. If the overall status is in
 * progress, the status is either the process status or in progress. If the overall status is
 * outsource, the status is either pending or ready assembly
 * @param {string} overallStatus - The overall status of the project.
 * @param {string} computedStatus - the status of the sample as computed by the pipeline
 * @param {string | null} [processStatus=null] - The status of the process.
 * @param {string | null} [assemblyStatus=null] - The status of the assembly process.
 * @returns The status of the sample.
 */
function getStatus(
  overallStatus: string,
  computedStatus: string,
  processStatus: string | null = null,
  assemblyStatus: string | null = null,
) {
  if (computedStatus === 'DONE') return computedStatus;

  if (overallStatus === 'ASSEMBLY') {
    if (assemblyStatus === 'DONE' || assemblyStatus === 'FAILED_ASSEMBLY') return assemblyStatus;
    return 'ASSEMBLY';
  }

  if (processStatus === 'QC' || computedStatus === 'QC_PASS') {
    if (overallStatus === 'QC_PASS') {
      if (assemblyStatus !== null && assemblyStatus !== 'PENDING') return assemblyStatus;
      return 'QC_PASS';
    }
    if (overallStatus === 'QC_PROGRESS') return 'QC_PROGRESS';
    if (overallStatus === 'QC_FAIL') return 'QC_FAIL';
  }

  if (overallStatus === 'REVISE' || overallStatus === 'MODIFY') return overallStatus;
  if (processStatus === 'OUTSOURCE') {
    if (computedStatus === 'PENDING') return 'OUTSOURCE';
    if (computedStatus === 'QC_PASS') return 'READY_ASSEMBLY';
    return computedStatus;
  }
  if (overallStatus === 'IN_PROGRESS') return processStatus !== 'PENDING' && processStatus ? processStatus : 'IN_PROGRESS';
  return computedStatus;
}

export const _primaryControls = {
  width: '3rem',
  name: 'เมนู',
  class: 'text-center',
};

export const _selectorControls = {
  width: '2rem',
  name: 'SELECT',
};

export const _increment = {
  name: 'ลำดับ',
  width: '3rem',
  class: 'text-center',
};

export const production_type = {
  name: 'ประเภท',
  width: '4rem',
};

export const job_no = {
  name: 'Sub Job No.',
  width: '10rem',
};

export const module_no = {
  name: 'Module No.',
  width: '6rem',
  class: 'text-center',
};

export const drawing_no = {
  name: 'Drawing / Part No.',
  width: '12rem',
  filter: (val: string, row: { item: { [key: string]: any }; index: number }) => row.item?.drawing_no || row.item.material?.code,
};

export const description = {
  name: 'Description',
  width: '10rem',
};

export const material_code = {
  name: 'Material / Brand',
  width: '10rem',
  filter: (val: string, row: any) => `${(row.item.material?.brand ? row.item.material?.brand : row.item.material?.name) || ''}`,
};

export const amount = {
  name: 'จำนวน',
  width: '4rem',
  class: 'text-right',
  filter: (val: string) => toNumberFormat(val),
};

export const qc_receive_amount = {
  name: 'QC รับเข้า',
  width: '4rem',
  class: 'text-right',
};

export const withdrawn_amount = {
  name: 'เบิก Store',
  width: '5rem',
  class: 'text-right',
  filter: (val: string) => toNumberFormat(val),
};

export const purchased_amount = {
  name: 'สั่งซื้อแล้ว',
  width: '5rem',
  class: 'text-right',
  filter: (val: string) => toNumberFormat(val),
};

export const unit = {
  name: 'หน่วย',
  width: '5rem',
};

export const status = {
  name: 'สถานะภาพรวม',
  width: '8rem',
  filter: (val: string, row: any) => translateSubJobStatus(getStatus(row.item.status, row.item.computed_status, row.item.process_status, row.item.assembly_status)),
  class: {
    text: (val: string, row: any) => statusClassMap(getStatus(row.item.status, row.item.computed_status, row.item.process_status, row.item.assembly_status)),
  },
};

export const leadTime = {
  name: 'Lead Time',
  width: '5rem',
  class: {
    cell: 'text-center',
    text: 'text-right',
  },
  filter: (val: string, row: any) => dateDifferenceDisplay(row.item.created_at, row.item.finish_date || new Date()),
};

// export const process_status = {
//   name: 'สถานะการผลิต',
//   width: '8rem',
//   filter: (val: string, row: any) => processTranslate(val, row.item.status),
//   class: {
//     text: (val: string, row: any) => processStatusClassMap(val, row.item.status),
//   },
// };

// export const assembly_status = {
//   name: 'การประกอบ',
//   width: '8rem',
//   filter: (val: string, row: any) => translateAssemblyStatus(val),
//   class: {
//     text: (val: string, row: any) => assemblyStatusClassMap(val),
//   },
// };

export const heat_treatment = {
  name: 'Heat Treatment',
  width: '8rem',
  class: 'text-center',
  filter: (val: string) => (val !== 'NONE' ? val : '-'),
};

export const surface_treatment = {
  name: 'Surface Treatment',
  width: '8rem',
  class: 'text-center',
  filter: (val: string) => (val !== 'NONE' ? val : '-'),
};

export const target_date = {
  name: 'กำหนดเสร็จ',
  width: '8rem',
  // eslint-disable-next-line
  filter: (val: string) => val ? useFormat.date(val).displayShortThaiDate() : '',
  class: 'text-right',
};

export const remarks = {
  name: 'หมายเหตุ',
  width: '8rem',
  class: 'text-right',
};

export const sortable = [
  'production_type',
  'job_no',
  'module_no',
  'drawing_no',
  'material_code',
  'heat_treatment',
  'surface_treatment',
  'target_date',
  'status',
  'process_status',
];

export const _startDateControls = {
  name: 'วันที่ส่ง',
  width: '10rem',
  class: 'text-center',
};

export const _finishDateControls = {
  name: 'วันที่รับกลับ',
  width: '10rem',
  class: 'text-center',
};

export const _remarksControls = {
  name: 'หมายเหตุ',
  width: '10rem',
  class: 'text-center',
};

export const created_at = {
  name: 'วันที่สร้าง',
  width: '8rem',
  class: 'text-right',
  // eslint-disable-next-line
  filter: (val: string) => val ? useFormat.date(val).displayShortThaiDate() : '',
};

export const updated_at = {
  name: 'อัปเดทล่าสุด',
  width: '8rem',
  class: 'text-right',
  // eslint-disable-next-line
  filter: (val: string) => val ? useFormat.date(val).displayShortThaiDate() : '',
};
